// 監控設定的 M2 分頁（`/sites/kmgr/:home/…`，SPEC #kinio-kms-ui「設定只有兩個家」，kmgr.configure）：
//   收集器（收集器負責的網段、網路設備 = ping / snmp 探針、〔測試連線〕）、探針（http / tcp / file）、
//   門檻與等級（SPEC #kinio-kms-alerts「預設等級與防抖」，kmgr 還沒有端點：mock）、維護與營業時段（M2-06 GET·PUT …/maintenance、…/business-hours）、
//   現場資訊（事故頁現場資訊卡的資料，kmgr 還沒有端點：mock）。
// SNMP 帳密要在瀏覽器用收集器的 cred_pubkey 做 HPKE 加密後送（CredentialRequest，TM-09）：這版還沒接，SNMP 探針先用既有的帳密參照。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { SkeletonLines } from '../../components/Skeleton';
import { Dialog } from '../../components/Dialog';
import type { MyOrg } from '../../lib/planeApi';
import { stamp, timeStamp } from '../../lib/timefmt';
import { kmgrApi } from './api';
import {
  DEFAULT_BUSINESS_WINDOW,
  DEFAULT_MAINTENANCE_WINDOW,
  debounceOptions,
  defaultThresholds,
  isNetworkDevice,
  MAINTENANCE_HOURS,
  normalizePhone,
  overriddenRules,
  parseCidrList,
  THRESHOLD_KEYS,
  validateProbe,
  validateThresholds,
  validateWindows,
  windowsText,
} from './model';
import { errText, NS, personName, StateBox } from './parts';
import { probeMethod, probeStateText, probeTarget, AppliedText } from './NetworkPage';
import { HostStatePill, ProbeStatePill, SiteInfoCard, WindowsEditor } from './widgets';
import type { HostRow, Probe, ProbeInput, ProbeKind, ProbeTestResult, Severity, SiteInfo, ThresholdRule, Thresholds, TimeWindow } from './types';

type TFn = (k: string, o?: Record<string, unknown>) => string;

function Loading() {
  const { t } = useTranslation(NS);
  return <SkeletonLines n={3} label={t('common.loading')} />;
}

function Unavailable({ error, retry }: { error: unknown; retry(): void }) {
  const { t } = useTranslation(NS);
  return (
    <StateBox icon="⊘" title={t('home_settings.unavailable')} body={errText(t, error)}>
      <button className="btn" onClick={retry}>
        {t('common.retry')}
      </button>
    </StateBox>
  );
}

function Msg({ msg }: { msg: { ok: boolean; text: string } | null }) {
  if (!msg) return null;
  return (
    <p className={msg.ok ? 'notice' : 'error'} role="status">
      {msg.text}
    </p>
  );
}

// ---- 收集器與網路設備 ----

export function CollectorsTab({ api, homeId, org }: { api: string; homeId: string; org?: MyOrg }) {
  const { t } = useTranslation(NS);
  const hosts = useQuery({ queryKey: ['kmgr', 'home-hosts', homeId], queryFn: () => kmgrApi.homeHosts(api, homeId), refetchInterval: 15_000 });
  const probes = useQuery({ queryKey: ['kmgr', 'probes', homeId], queryFn: () => kmgrApi.probes(api, homeId), refetchInterval: 15_000 });
  const status = useQuery({ queryKey: ['kmgr', 'home', homeId], queryFn: () => kmgrApi.homeStatus(api, homeId), staleTime: 15_000 });
  const policy = useQuery({ queryKey: ['kmgr', 'policy', org?.org_id], queryFn: () => kmgrApi.securityPolicy(org!.api, org!.org_id), enabled: !!org && (org.role === 'owner' || org.role === 'org_admin'), staleTime: 60_000, retry: false });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Probe | null>(null);
  if (hosts.isLoading || probes.isLoading) return <Loading />;
  if (hosts.isError && !hosts.data) return <Unavailable error={hosts.error} retry={() => void hosts.refetch()} />;
  const collectors = (hosts.data ?? []).filter((h) => h.collector);
  const candidates = (hosts.data ?? []).filter((h) => !h.collector && h.state === 'active' && h.license_state === 'licensed');
  const unreadable = status.data?.network?.source_state === 'unreadable';
  const devices = (probes.data ?? []).filter(isNetworkDevice);
  return (
    <div data-testid="km-collectors">
      <p className="muted" style={{ marginTop: 0 }}>
        {t('collector.intro')}
      </p>
      {policy.data && <p className="km-sub muted">{policy.data.network_scan.length ? t('collector.policy_scan', { cidrs: policy.data.network_scan.join('、') }) : t('collector.policy_none')}</p>}
      {collectors.length ? (
        collectors.map((c) => <CollectorCard key={c.target_ref} c={c} api={api} homeId={homeId} />)
      ) : (
        <StateBox icon="○" title={t('collector.none')} body={t('collector.none_body')} testId="km-collectors-empty">
          {candidates.length > 0 && <MakeCollector api={api} homeId={homeId} candidates={candidates} />}
        </StateBox>
      )}
      <div className="km-block-head" style={{ marginTop: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{t('net.tab_devices_n', { n: devices.length })}</h2>
        <span className="spacer" />
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)} disabled={!collectors.length}>
          {t('collector.add_device')}
        </button>
      </div>
      {!collectors.length && <p className="muted km-sub">{t('collector.need_collector')}</p>}
      {devices.length > 0 && (
        <table className="km-table" data-testid="km-devices">
          <thead>
            <tr>
              <th>{t('net.col_name')}</th>
              <th>{t('net.col_addr')}</th>
              <th>{t('net.col_method')}</th>
              <th>{t('hosts.col_state')}</th>
              <th>{t('hosts.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((p) => {
              const st = probeStateText(t, p, !!unreadable);
              return (
                <tr key={p.probe_id} data-probe={p.probe_id}>
                  <td>
                    <strong>{p.name || p.target}</strong>
                  </td>
                  <td className="tabular">{p.target}</td>
                  <td>{probeMethod(t, p)}</td>
                  <td>
                    <ProbeStatePill state={st.state} text={st.text} />
                  </td>
                  <td>
                    <button type="button" className="btn btn-sm" onClick={() => setEditing(p)}>
                      {t('probe.edit')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="muted km-sub">{t('collector.kinio_note')}</p>
      {(adding || editing) && <ProbeDialog api={api} homeId={homeId} kinds={['ping', 'snmp']} probe={editing ?? undefined} collectors={collectors} onClose={() => (setAdding(false), setEditing(null))} />}
    </div>
  );
}

/** 一台收集器：這台電腦、可以掃的網段（整份取代 POST …/collectors/{device}/assignments）、套用狀態。 */
function CollectorCard({ c, api, homeId }: { c: HostRow; api: string; homeId: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const [text, setText] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const cur = c.collector!.assignments.join('\n');
  const val = text ?? cur;
  const [ok, bad] = parseCidrList(val);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await kmgrApi.assignCollector(api, homeId, c.target_ref, ok);
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
      setText(undefined);
      setMsg({ ok: true, text: t('collector.saved') });
    } catch (e) {
      setMsg({ ok: false, text: isKinioError(e) && e.code === 'NETWORK_SCAN_NOT_ALLOWED' ? t('collector.err_scan', { target: String(e.detail?.target ?? '') }) : errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" data-testid="km-collector" data-host={c.target_ref}>
      <div className="km-block-head">
        <h2 style={{ margin: 0, fontSize: 16 }}>
          {t('collector.title')} {c.hostname}
        </h2>
        <HostStatePill h={c} />
        <span className="spacer" />
        <AppliedText c={c.collector!} />
      </div>
      <dl className="km-dl">
        <dt>{t('host.this_pc')}</dt>
        <dd>{[c.hostname, c.os ? `${c.os.name} ${c.os.version}` : ''].filter(Boolean).join(' · ')}</dd>
        <dt>{t('collector.scan')}</dt>
        <dd>
          <textarea className="input" rows={2} value={val} onChange={(e) => setText(e.target.value)} aria-label={t('collector.scan')} placeholder="10.20.0.0/16" />
          <span className="hint km-sub muted" style={{ display: 'block' }}>
            {t('collector.scan_hint')}
          </span>
          {bad.length > 0 && (
            <span className="error km-sub" role="alert">
              {t('token.cidrs_bad', { list: bad.join(', ') })}
            </span>
          )}
        </dd>
      </dl>
      <Msg msg={msg} />
      <div className="toolbar wrap" style={{ marginBottom: 0 }}>
        <button className="btn btn-primary btn-sm" disabled={busy || val === cur || bad.length > 0} onClick={() => void save()}>
          {busy ? t('common.saving') : t('collector.save_scan')}
        </button>
        <span className="muted km-sub">{t('collector.local_note')}</span>
      </div>
    </section>
  );
}

/** 還沒有收集器：選一台已上線的電腦當收集器（指派網段就成為收集器）。 */
function MakeCollector({ api, homeId, candidates }: { api: string; homeId: string; candidates: HostRow[] }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const [dev, setDev] = useState(candidates.find((c) => c.presence === 'online')?.target_ref ?? candidates[0].target_ref);
  const [cidr, setCidr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, bad] = parseCidrList(cidr);
  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      await kmgrApi.assignCollector(api, homeId, dev, ok);
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
    } catch (e) {
      setErr(isKinioError(e) && e.code === 'NETWORK_SCAN_NOT_ALLOWED' ? t('collector.err_scan', { target: String(e.detail?.target ?? '') }) : errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="km-form" style={{ textAlign: 'left', marginTop: 12 }} data-testid="km-make-collector">
      <label className="field">
        <span>{t('collector.pick')}</span>
        <select className="input" value={dev} onChange={(e) => setDev(e.target.value)}>
          {candidates.map((c) => (
            <option key={c.target_ref} value={c.target_ref}>
              {c.hostname || c.target_ref}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('collector.scan')}</span>
        <input className="input" value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="10.20.0.0/16" />
        {bad.length > 0 && <span className="hint bad">{t('token.cidrs_bad', { list: bad.join(', ') })}</span>}
      </label>
      {err && (
        <p className="error span-2" role="alert">
          {err}
        </p>
      )}
      <div className="span-2">
        <button className="btn btn-primary" disabled={busy || !ok.length || bad.length > 0} onClick={() => void run()}>
          {t('collector.make')}
        </button>
      </div>
    </div>
  );
}

// ---- 探針（新增 / 修改 / 刪除 / 測試連線） ----

export function ProbesTab({ api, homeId }: { api: string; homeId: string }) {
  const { t } = useTranslation(NS);
  const probes = useQuery({ queryKey: ['kmgr', 'probes', homeId], queryFn: () => kmgrApi.probes(api, homeId), refetchInterval: 15_000 });
  const hosts = useQuery({ queryKey: ['kmgr', 'home-hosts', homeId], queryFn: () => kmgrApi.homeHosts(api, homeId), staleTime: 15_000 });
  const status = useQuery({ queryKey: ['kmgr', 'home', homeId], queryFn: () => kmgrApi.homeStatus(api, homeId), staleTime: 15_000 });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Probe | null>(null);
  if (probes.isLoading) return <Loading />;
  if (probes.isError && !probes.data) return <Unavailable error={probes.error} retry={() => void probes.refetch()} />;
  const apps = (probes.data ?? []).filter((p) => !isNetworkDevice(p));
  const collectors = (hosts.data ?? []).filter((h) => h.collector);
  const unreadable = status.data?.network?.source_state === 'unreadable';
  return (
    <div data-testid="km-probes">
      <div className="km-block-head">
        <p className="muted" style={{ margin: 0 }}>
          {t('probe.intro')}
        </p>
        <span className="spacer" />
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
          {t('probe.add')}
        </button>
      </div>
      {apps.length ? (
        <table className="km-table">
          <thead>
            <tr>
              <th>{t('net.col_name')}</th>
              <th>{t('net.col_target')}</th>
              <th>{t('net.col_kind')}</th>
              <th>{t('probe.interval')}</th>
              <th>{t('hosts.col_state')}</th>
              <th>{t('hosts.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {apps.map((p) => {
              const st = probeStateText(t, p, !!unreadable);
              return (
                <tr key={p.probe_id} data-probe={p.probe_id}>
                  <td>
                    <strong>{p.name || probeTarget(p)}</strong>
                  </td>
                  <td className="tabular">{probeTarget(p)}</td>
                  <td>{t(`probe.kind_${p.kind}`)}</td>
                  <td>{p.interval_s ? t('probe.every_s', { n: p.interval_s }) : '—'}</td>
                  <td>
                    <ProbeStatePill state={st.state} text={st.text} />
                    {p.last_at && <span className="sub">{stamp(p.last_at)}</span>}
                  </td>
                  <td>
                    <button type="button" className="btn btn-sm" onClick={() => setEditing(p)}>
                      {t('probe.edit')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <StateBox icon="○" title={t('net.probes_none')} body={t('probe.none_body')} testId="km-probes-empty" />
      )}
      {(adding || editing) && <ProbeDialog api={api} homeId={homeId} kinds={['http', 'tcp', 'file']} probe={editing ?? undefined} collectors={collectors} onClose={() => (setAdding(false), setEditing(null))} />}
    </div>
  );
}

/** 新增 / 修改探針：種類、名稱、目標（埠 / 路徑 / 帳密參照 / 收集器）、〔測試連線〕、〔刪除〕。目標不在組織允許網段 → 422 NETWORK_SCAN_NOT_ALLOWED 白話。 */
function ProbeDialog({ api, homeId, kinds, probe, collectors, onClose }: { api: string; homeId: string; kinds: ProbeKind[]; probe?: Probe; collectors: HostRow[]; onClose(): void }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const [kind, setKind] = useState<ProbeKind>(probe?.kind ?? kinds[0]);
  const [name, setName] = useState(probe?.name ?? '');
  const [target, setTarget] = useState(probe?.target ?? '');
  const [port, setPort] = useState(probe?.port ? String(probe.port) : '');
  const [scheme, setScheme] = useState<'http' | 'https'>(probe?.http?.scheme ?? 'https');
  const [path, setPath] = useState(probe?.http?.path ?? '/');
  const [keyword, setKeyword] = useState(probe?.http?.keyword ?? '');
  const [file, setFile] = useState(probe?.file?.path ?? '');
  const [cred, setCred] = useState(probe?.credential_ref ?? '');
  const [collector, setCollector] = useState(probe?.collector ?? '');
  const [interval, setInterval_] = useState(String(probe?.interval_s ?? (kind === 'file' ? 3600 : 60)));
  const [busy, setBusy] = useState<'save' | 'test' | 'delete' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [test, setTest] = useState<ProbeTestResult | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const input = (): ProbeInput => ({
    kind,
    name: name.trim() || undefined,
    target: kind === 'file' ? undefined : target.trim(),
    port: kind === 'tcp' ? Number(port) : undefined,
    interval_s: Number(interval) || undefined,
    credential_ref: kind === 'snmp' && cred.trim() ? cred.trim() : undefined,
    collector: collector || undefined,
    http: kind === 'http' ? { scheme, path, expect_status: [200], keyword: keyword.trim() || undefined } : undefined,
    file: kind === 'file' ? { path: file.trim() } : undefined,
  });
  const errs = validateProbe({ kind, name, target, port: Number(port), http: { path }, file: { path: file } });
  const fail = (e: unknown) => {
    if (isKinioError(e) && e.code === 'NETWORK_SCAN_NOT_ALLOWED') return t('collector.err_scan', { target: target.trim() });
    if (isKinioError(e) && e.status === 503 && e.detail?.reason === 'collector_offline') return t('probe.err_collector_offline');
    if (isKinioError(e) && e.status === 409 && e.detail?.reason === 'collector_disabled') return t('probe.err_collector_disabled');
    return errText(t, e);
  };
  const run = async (what: 'save' | 'test' | 'delete') => {
    setBusy(what);
    setErr(null);
    try {
      if (what === 'test') setTest(await kmgrApi.testProbe(api, homeId, input()));
      else if (what === 'delete') {
        await kmgrApi.deleteProbe(api, homeId, probe!.probe_id);
        await qc.invalidateQueries({ queryKey: ['kmgr'] });
        onClose();
      } else {
        if (probe) await kmgrApi.putProbe(api, homeId, probe.probe_id, input());
        else await kmgrApi.createProbe(api, homeId, input());
        await qc.invalidateQueries({ queryKey: ['kmgr'] });
        onClose();
      }
    } catch (e) {
      setErr(fail(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog open title={probe ? t('probe.edit_title') : t('probe.add_title')} onClose={onClose} testId="km-probe-dialog" wide>
      <div className="km-form">
        <label className="field">
          <span>{t('net.col_kind')}</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as ProbeKind)} disabled={!!probe}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {t(`probe.kind_${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('net.col_name')}</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </label>
        {kind !== 'file' && (
          <label className="field">
            <span>{kind === 'http' ? t('probe.host') : t('net.col_addr')}</span>
            <input className="input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={kind === 'http' ? 'erp.example.internal' : '10.20.0.2'} />
          </label>
        )}
        {kind === 'tcp' && (
          <label className="field">
            <span>{t('probe.port')}</span>
            <input className="input" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} />
          </label>
        )}
        {kind === 'http' && (
          <>
            <label className="field">
              <span>{t('probe.scheme')}</span>
              <select className="input" value={scheme} onChange={(e) => setScheme(e.target.value as 'http' | 'https')}>
                <option value="https">https</option>
                <option value="http">http</option>
              </select>
            </label>
            <label className="field">
              <span>{t('probe.path')}</span>
              <input className="input" value={path} onChange={(e) => setPath(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('probe.keyword')}</span>
              <input className="input" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
              <span className="hint">{t('probe.http_hint')}</span>
            </label>
          </>
        )}
        {kind === 'file' && (
          <label className="field span-2">
            <span>{t('probe.file')}</span>
            <input className="input" value={file} onChange={(e) => setFile(e.target.value)} placeholder="\\\\SRV-FILE02\\backup\\pos.bak" />
            <span className="hint">{t('probe.file_hint')}</span>
          </label>
        )}
        {kind === 'snmp' && (
          <label className="field">
            <span>{t('probe.credential')}</span>
            <input className="input" value={cred} onChange={(e) => setCred(e.target.value)} />
            <span className="hint">{t('probe.credential_hint')}</span>
          </label>
        )}
        <label className="field">
          <span>{t('probe.interval')}</span>
          <select className="input" value={interval} onChange={(e) => setInterval_(e.target.value)}>
            {[30, 60, 300, 900, 3600].map((n) => (
              <option key={n} value={n}>
                {t('probe.every_s', { n })}
              </option>
            ))}
          </select>
        </label>
        {collectors.length > 0 && (
          <label className="field">
            <span>{t('net.col_collector')}</span>
            <select className="input" value={collector} onChange={(e) => setCollector(e.target.value)}>
              <option value="">{t('probe.collector_auto')}</option>
              {collectors.map((c) => (
                <option key={c.target_ref} value={c.target_ref}>
                  {c.hostname || c.target_ref}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {errs.length > 0 && <p className="muted km-sub">{errs.map((k) => t(k)).join('；')}</p>}
      {test && (
        <div className={'km-bar ' + (test.up ? 'ok' : 'warn')} role="status" data-testid="km-probe-test">
          <span className="ic" aria-hidden>
            {test.up ? '✓' : '!'}
          </span>
          <span className="txt">{testText(t, test)}</span>
        </div>
      )}
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        {probe && (
          <button className="btn" style={{ marginRight: 'auto' }} disabled={!!busy} onClick={() => setConfirmDelete(true)}>
            {t('probe.delete')}
          </button>
        )}
        <button className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button className="btn" disabled={!!busy || errs.length > 0} onClick={() => void run('test')}>
          {busy === 'test' ? t('common.loading') : t('probe.test')}
        </button>
        <button className="btn btn-primary" disabled={!!busy || errs.length > 0} onClick={() => void run('save')}>
          {busy === 'save' ? t('common.saving') : probe ? t('notify.save') : t('probe.create')}
        </button>
      </div>
      {confirmDelete && (
        <Dialog open title={t('probe.delete_title', { name: probe?.name || probeTarget(probe!) })} onClose={() => setConfirmDelete(false)} testId="km-probe-delete">
          <p className="muted">{t('probe.delete_body')}</p>
          <div className="dialog-actions">
            <button className="btn" onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-danger" disabled={!!busy} onClick={() => void run('delete')}>
              {t('probe.delete')}
            </button>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}

/** 測試結果一句：可以讀取 · 24 個介面 · 2 ms / HTTP 200 · 憑證 88 天 / 失敗原因（白話）。 */
export function testText(t: TFn, r: ProbeTestResult): string {
  if (r.up) {
    const parts = [t('probe.test_ok')];
    if (r.interfaces) parts.push(t('net.ifs_n', { n: r.interfaces }));
    if (r.http_status) parts.push(`HTTP ${r.http_status}`);
    if (r.tls_expiry_days !== undefined) parts.push(t('probe.tls_days', { n: Math.round(r.tls_expiry_days) }));
    if (r.latency_ms !== undefined) parts.push(`${Math.round(r.latency_ms * 10) / 10} ms`);
    return parts.join(' · ');
  }
  return t(`probe.test_${r.error_code ?? 'failed'}`, { defaultValue: t('probe.test_failed') }) + (r.message ? `：${r.message}` : '');
}

// ---- 門檻與等級 ----

export function ThresholdsTab({ api, homeId }: { api: string; homeId: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['kmgr', 'thresholds', homeId], queryFn: () => kmgrApi.thresholds(api, homeId), retry: false });
  const [draft, setDraft] = useState<Thresholds | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.isError && !q.data) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const cur = q.data ?? defaultThresholds(homeId);
  const v = draft ?? cur;
  const changed = overriddenRules(v.rules);
  const errs = validateThresholds(v);
  const dirty = JSON.stringify(v) !== JSON.stringify(cur);
  const setRule = (key: string, patch: Partial<ThresholdRule>) => setDraft({ ...v, rules: v.rules.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await kmgrApi.setThresholds(api, homeId, { rules: v.rules, disk_pct: v.disk_pct, tls_days: v.tls_days, workstation_offline_days: v.workstation_offline_days });
      await qc.invalidateQueries({ queryKey: ['kmgr', 'thresholds', homeId] });
      setDraft(null);
      setMsg({ ok: true, text: t('threshold.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card km-rules" data-testid="km-thresholds">
      <p className="muted" style={{ marginTop: 0 }}>
        {t('threshold.intro')}
      </p>
      <table className="km-table">
        <thead>
          <tr>
            <th>{t('threshold.col_condition')}</th>
            <th>{t('threshold.col_severity')}</th>
            <th>{t('threshold.col_debounce')}</th>
          </tr>
        </thead>
        <tbody>
          {THRESHOLD_KEYS.map((key) => {
            const r = v.rules.find((x) => x.key === key) ?? { key, severity: 'P3' as Severity, debounce_min: null };
            return (
              <tr key={key} data-rule={key}>
                <td>
                  {t(`threshold.rule_${key}`)}
                  {changed.has(key) && <span className="changed">{t('threshold.changed')}</span>}
                </td>
                <td>
                  <select className="input" value={r.severity} aria-label={t('threshold.col_severity')} onChange={(e) => setRule(key, { severity: e.target.value as Severity })}>
                    {(['P1', 'P2', 'P3'] as Severity[]).map((s) => (
                      <option key={s} value={s}>
                        {t(`sev.${s}`)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select className="input" value={r.debounce_min === null ? 'none' : String(r.debounce_min)} aria-label={t('threshold.col_debounce')} onChange={(e) => setRule(key, { debounce_min: e.target.value === 'none' ? null : Number(e.target.value) })}>
                    {debounceOptions(key, r.debounce_min).map((d) => (
                      <option key={String(d)} value={d === null ? 'none' : String(d)}>
                        {d === null ? t('threshold.no_incident') : d === 0 ? t('threshold.immediate') : t('threshold.minutes', { n: d })}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="km-form" style={{ marginTop: 12 }}>
        <label className="field">
          <span>{t('threshold.disk_pct')}</span>
          <input className="input" inputMode="numeric" value={v.disk_pct} onChange={(e) => setDraft({ ...v, disk_pct: Number(e.target.value) })} />
        </label>
        <label className="field">
          <span>{t('threshold.tls_days')}</span>
          <input className="input" inputMode="numeric" value={v.tls_days} onChange={(e) => setDraft({ ...v, tls_days: Number(e.target.value) })} />
        </label>
        <label className="field">
          <span>{t('threshold.ws_days')}</span>
          <input className="input" inputMode="numeric" value={v.workstation_offline_days} onChange={(e) => setDraft({ ...v, workstation_offline_days: Number(e.target.value) })} />
        </label>
      </div>
      {errs.length > 0 && (
        <p className="error km-sub" role="alert">
          {errs.map((k) => t(k)).join('；')}
        </p>
      )}
      <Msg msg={msg} />
      {cur.updated_at && <p className="muted km-sub">{t('threshold.updated', { name: personName(t, cur.updated_by), time: stamp(cur.updated_at) })}</p>}
      <div className="km-dock">
        <button type="button" className="btn" onClick={() => setDraft(defaultThresholds(homeId))}>
          {t('threshold.reset')}
        </button>
        <span className="why">{t('threshold.save_note')}</span>
        <button className="btn btn-primary" disabled={!dirty || busy || errs.length > 0} onClick={() => void save()}>
          {busy ? t('common.saving') : t('notify.save')}
        </button>
      </div>
    </section>
  );
}

// ---- 維護與營業時段（M2-06） ----

export function WindowsTab({ api, homeId }: { api: string; homeId: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const maint = useQuery({ queryKey: ['kmgr', 'maintenance', homeId], queryFn: () => kmgrApi.maintenance(api, homeId), refetchInterval: 30_000 });
  const biz = useQuery({ queryKey: ['kmgr', 'business', homeId], queryFn: () => kmgrApi.businessHours(api, homeId) });
  const [hours, setHours] = useState(2);
  const [schedule, setSchedule] = useState<TimeWindow[] | undefined>(undefined);
  const [bizWin, setBizWin] = useState<TimeWindow[] | undefined>(undefined);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [bizMsg, setBizMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei';
  const dayName = (d: number) => t(`day.${d}`);
  if (maint.isLoading || biz.isLoading) return <Loading />;
  if (maint.isError && !maint.data) return <Unavailable error={maint.error} retry={() => void maint.refetch()} />;
  const m = maint.data!;
  const sched = schedule ?? m.schedule?.windows ?? [];
  const schedErrs = validateWindows(sched);
  const schedDirty = JSON.stringify(sched) !== JSON.stringify(m.schedule?.windows ?? []);
  const bw = bizWin ?? biz.data?.windows ?? [];
  const bizErrs = validateWindows(bw);
  const bizDirty = JSON.stringify(bw) !== JSON.stringify(biz.data?.windows ?? []);
  const put = async (what: string, body: Parameters<typeof kmgrApi.setMaintenance>[2]) => {
    setBusy(what);
    setMsg(null);
    try {
      const r = await kmgrApi.setMaintenance(api, homeId, body);
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
      setSchedule(undefined);
      setMsg({ ok: true, text: r.changed === false ? t('maint.unchanged') : r.notified ? t('maint.saved_notified', { n: r.notified }) : t('maint.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(null);
    }
  };
  const saveBiz = async () => {
    setBusy('biz');
    setBizMsg(null);
    try {
      await kmgrApi.setBusinessHours(api, homeId, { tz: biz.data?.tz || tz, windows: bw });
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
      setBizWin(undefined);
      setBizMsg({ ok: true, text: t('biz.saved') });
    } catch (e) {
      setBizMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <div data-testid="km-windows-tab">
      <section className="card" data-testid="km-maintenance" data-active={m.active ? '1' : '0'}>
        <h2 style={{ marginTop: 0, fontSize: 16 }}>{t('maint.title')}</h2>
        {m.active ? (
          <div className="km-bar" style={{ background: 'var(--km-maint-soft)', borderColor: 'var(--km-maint)' }} role="status">
            <span className="ic" aria-hidden style={{ color: 'var(--km-maint)' }}>
              ⏸
            </span>
            <span className="txt">
              <strong>{t('home.maintenance_until', { time: timeStamp(m.active_until ?? m.until ?? '', undefined, false) })}</strong>
              {m.set_by && <span className="km-sub"> · {t('maint.set_by', { name: personName(t, m.set_by), time: m.set_at ? stamp(m.set_at) : '' })}</span>}
              <br />
              <span className="km-sub">{t('maint.active_note')}</span>
            </span>
            <button className="btn btn-sm" disabled={!!busy} onClick={() => void put('end', { until: null })}>
              {t('maint.end')}
            </button>
          </div>
        ) : (
          <p className="muted km-sub">{t('maint.inactive')}</p>
        )}
        <div className="toolbar wrap" style={{ alignItems: 'flex-end' }}>
          <label className="field" style={{ margin: 0 }}>
            <span>{m.active ? t('maint.extend_label') : t('maint.quick_label')}</span>
            <select className="input" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              {MAINTENANCE_HOURS.map((h) => (
                <option key={h} value={h}>
                  {t('maint.hours_n', { n: h })}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-primary" disabled={!!busy} onClick={() => void put('quick', { hours })} data-testid="km-maint-quick">
            {m.active ? t('maint.extend') : t('maint.start', { n: hours })}
          </button>
        </div>
        <p className="muted km-sub">{t('maint.quick_note')}</p>
        <h3 className="km-todo-title">{t('maint.schedule')}</h3>
        <p className="muted km-sub">{sched.length ? windowsText({ tz, windows: sched }, dayName, t('window.every_day'), t('window.all_day')) : t('maint.schedule_none')}</p>
        <WindowsEditor value={sched} onChange={setSchedule} addDefault={DEFAULT_MAINTENANCE_WINDOW} label={t('maint.schedule')} />
        {schedErrs.length > 0 && (
          <p className="error km-sub" role="alert">
            {schedErrs.map((k) => t(k)).join('；')}
          </p>
        )}
        <Msg msg={msg} />
        <div className="km-dock">
          <span className="why">{t('maint.save_note')}</span>
          <button className="btn btn-primary" disabled={!schedDirty || !!busy || schedErrs.length > 0} onClick={() => void put('schedule', { schedule: sched.length ? { tz: m.schedule?.tz || tz, windows: sched } : null })}>
            {busy === 'schedule' ? t('common.saving') : t('maint.save_schedule')}
          </button>
        </div>
      </section>
      <section className="card" data-testid="km-business">
        <h2 style={{ marginTop: 0, fontSize: 16 }}>{t('biz.title')}</h2>
        <p className="muted km-sub">{t('biz.intro')}</p>
        {biz.isError && !biz.data ? (
          <p className="error km-sub">{errText(t, biz.error)}</p>
        ) : (
          <>
            <p className="muted km-sub">{bw.length ? windowsText({ tz, windows: bw }, dayName, t('window.every_day'), t('window.all_day')) : t('biz.all_day')}</p>
            <WindowsEditor value={bw} onChange={setBizWin} addDefault={DEFAULT_BUSINESS_WINDOW} label={t('biz.title')} />
            {bizErrs.length > 0 && (
              <p className="error km-sub" role="alert">
                {bizErrs.map((k) => t(k)).join('；')}
              </p>
            )}
            <Msg msg={bizMsg} />
            <div className="km-dock">
              <span className="why">{t('biz.save_note')}</span>
              <button className="btn btn-primary" disabled={!bizDirty || !!busy || bizErrs.length > 0} onClick={() => void saveBiz()}>
                {busy === 'biz' ? t('common.saving') : t('notify.save')}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

// ---- 現場資訊 ----

const SITE_FIELDS = ['service_level', 'contract_until', 'contact_role', 'contact_name', 'contact_phone', 'it_support', 'rack_note', 'access_note'] as const;
type SiteField = (typeof SITE_FIELDS)[number];

export function SiteInfoTab({ api, homeId, orgName }: { api: string; homeId: string; orgName?: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['kmgr', 'site', homeId], queryFn: () => kmgrApi.siteInfo(api, homeId), retry: false });
  const [draft, setDraft] = useState<Partial<Record<SiteField, string>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.isError && !q.data) return <Unavailable error={q.error} retry={() => void q.refetch()} />;
  const cur = q.data ?? { home_id: homeId };
  const v: Partial<Record<SiteField, string>> = draft ?? Object.fromEntries(SITE_FIELDS.map((k) => [k, (cur as SiteInfo)[k] ?? '']));
  const phoneOk = normalizePhone(v.contact_phone ?? '') !== null;
  const dirty = SITE_FIELDS.some((k) => (v[k] ?? '') !== ((cur as SiteInfo)[k] ?? ''));
  const preview: SiteInfo = { home_id: homeId, ...Object.fromEntries(SITE_FIELDS.map((k) => [k, v[k]?.trim() || undefined])) };
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { home_id: _h, ...body } = preview;
      await kmgrApi.setSiteInfo(api, homeId, body);
      await qc.invalidateQueries({ queryKey: ['kmgr', 'site', homeId] });
      setDraft(null);
      setMsg({ ok: true, text: t('site.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const field = (k: SiteField, opts: { span?: boolean; textarea?: boolean; type?: string; placeholder?: string } = {}) => (
    <label className={'field' + (opts.span ? ' span-2' : '')} key={k}>
      <span>{t(`site.f_${k}`)}</span>
      {opts.textarea ? (
        <textarea className="input" rows={2} value={v[k] ?? ''} onChange={(e) => setDraft({ ...v, [k]: e.target.value })} maxLength={500} />
      ) : (
        <input className="input" type={opts.type ?? 'text'} value={v[k] ?? ''} onChange={(e) => setDraft({ ...v, [k]: e.target.value })} placeholder={opts.placeholder} maxLength={120} />
      )}
      {t(`site.h_${k}`, { defaultValue: '' }) && <span className="hint">{t(`site.h_${k}`)}</span>}
    </label>
  );
  return (
    <div data-testid="km-site-tab">
      <p className="muted" style={{ marginTop: 0 }}>
        {t('site.intro')}
      </p>
      <section className="card">
        <div className="km-form">
          {field('service_level', { placeholder: '24×7' })}
          {field('contract_until', { type: 'date' })}
          {field('contact_role', { placeholder: t('site.ph_role') })}
          {field('contact_name')}
          {field('contact_phone', { type: 'tel', placeholder: '02-2712-3456' })}
          {field('it_support', { placeholder: t('site.ph_it') })}
          {field('rack_note', { span: true, textarea: true })}
          {field('access_note', { span: true, textarea: true })}
        </div>
        {!phoneOk && (
          <p className="error km-sub" role="alert">
            {t('site.err_phone')}
          </p>
        )}
        <Msg msg={msg} />
        {cur.updated_at && <p className="muted km-sub">{t('threshold.updated', { name: personName(t, cur.updated_by), time: stamp(cur.updated_at) })}</p>}
        <div className="km-dock">
          <span className="why">{t('site.save_note')}</span>
          <button className="btn btn-primary" disabled={!dirty || busy || !phoneOk} onClick={() => void save()}>
            {busy ? t('common.saving') : t('notify.save')}
          </button>
        </div>
      </section>
      <h2 className="km-todo-title">{t('site.preview')}</h2>
      <SiteInfoCard info={preview} orgName={orgName} collapsible={false} />
    </div>
  );
}
